<template>
    <div id="telnyx-cdr">
        <div class="page-header">
            <div class="left">
                <h3>Arama Kayıtları (Recordings)</h3>
                <p>Telnyx arama kayıtlarını görüntüleyin</p>
            </div>
            <div class="right">
                <a-button @click="refreshData" :loading="loading" :icon="'reload'"> Yenile </a-button>
                <a-button type="primary" @click="exportRecordings" :loading="loading" :icon="'download'">
                    Dışa Aktar
                </a-button>
            </div>
        </div>

        <!-- Advanced Filters -->
        <div class="toolbar">
            <div class="toolbar-filter">
                <a-input-search
                    style="width: 300px"
                    v-model="searchText"
                    placeholder="Call Leg ID, Session ID veya Connection ID ile ara..."
                    class="search-input"
                    :loading="loading"
                    @search="onSearch"
                />
                <!-- Date Range Filter (Always visible and required) -->
                <div class="date-filter-inline">
                    <span class="date-label">Tarih Aralığı:</span>
                    <a-range-picker
                        v-model="dateRange"
                        format="DD.MM.YYYY"
                        style="width: 250px"
                        :placeholder="['Başlangıç', 'Bitiş']"
                        @change="onDateRangeChange"
                    />
                    <span class="date-required">*</span>
                </div>
                <CommonFilterTool
                    v-model="breakdownFilter"
                    :keep-state-on-query-params="false"
                    :filter-meta="filterMetas"
                    :label-formatter="filterLabelFormatter"
                    @addFilter="addFilter"
                    @removeFilter="removeFilter"
                    @clearAll="clearFilters"
                    :show-clear-all="breakdownFilter.length > 0"
                />
            </div>
            <div class="toolbar-actions"></div>
        </div>

        <!-- Statistics -->
        <div class="statistics">
            <div class="stats-grid">
                <div class="stat-card total-recordings">
                    <div class="stat-icon">
                        <i class="material-icons">record_voice_over</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.totalRecordings }}</div>
                        <div class="stat-label">Toplam Kayıt</div>
                    </div>
                </div>

                <div class="stat-card inbound-recordings">
                    <div class="stat-icon">
                        <i class="material-icons">call_received</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.inboundRecordings }}</div>
                        <div class="stat-label">Gelen Aramalar</div>
                        <div class="stat-percentage">
                            {{
                                statistics.totalRecordings > 0
                                    ? Math.round((statistics.inboundRecordings / statistics.totalRecordings) * 100)
                                    : 0
                            }}%
                        </div>
                    </div>
                </div>

                <div class="stat-card outbound-recordings">
                    <div class="stat-icon">
                        <i class="material-icons">call_made</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.outboundRecordings }}</div>
                        <div class="stat-label">Giden Aramalar</div>
                        <div class="stat-percentage">
                            {{
                                statistics.totalRecordings > 0
                                    ? Math.round((statistics.outboundRecordings / statistics.totalRecordings) * 100)
                                    : 0
                            }}%
                        </div>
                    </div>
                </div>

                <div class="stat-card average-duration">
                    <div class="stat-icon">
                        <i class="material-icons">timer</i>
                    </div>
                    <div class="stat-content">
                        <div class="stat-value">{{ statistics.averageDuration }}</div>
                        <div class="stat-label">Ortalama Süre</div>
                        <div class="stat-unit">saniye</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Recordings Table -->
        <div class="content">
            <SupsisTable
                :columns="getCols()"
                :rows="recordingData"
                :loading="loading"
                top-pagination
                bottom-pagination
                :pagination-cursor="pagination"
                :options="{
                    fixedHeader: true,
                    borderX: true,
                    borderY: true,
                }"
                @cursor-changed="onPaginationChange"
                :pagination-options="[10, 20, 30, 40, 50, 100, 200, 250]"
            />
        </div>

        <!-- Recording Detail Modal -->
        <a-modal v-model="showDetailModal" title="Kayıt Detayları" :footer="null" width="700px">
            <div v-if="selectedRecording">
                <a-descriptions :column="2" bordered size="small">
                    <a-descriptions-item label="Recording ID">
                        {{ selectedRecording.id }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Oluşturma Tarihi">
                        {{ $moment(selectedRecording.created_at).format("DD.MM.YYYY HH:mm:ss") }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Yön">
                        <a-tag :color="getDirectionColor(determineDirection(selectedRecording))">
                            {{ getDirectionTextFromDirection(determineDirection(selectedRecording)) }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="Arayan (From)">
                        {{ selectedRecording.from || "-" }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Aranan (To)">
                        {{ selectedRecording.to || "-" }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Durum">
                        <a-tag :color="getStatusColor(selectedRecording.status)">
                            {{ getStatusText(selectedRecording.status) }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="Kaynak">
                        <a-tag :color="getSourceColor(selectedRecording.source)">
                            {{ getSourceText(selectedRecording.source) }}
                        </a-tag>
                    </a-descriptions-item>
                    <a-descriptions-item label="Süre">
                        {{ formatDuration(selectedRecording.duration_millis) }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Dosya Boyutu">
                        {{ formatFileSize(selectedRecording.file_size) }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Kanal Sayısı">
                        {{ selectedRecording.channels }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Conference ID" v-if="selectedRecording.conference_id">
                        {{ selectedRecording.conference_id }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Call Leg ID" v-if="selectedRecording.call_leg_id">
                        {{ selectedRecording.call_leg_id }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Call Session ID" v-if="selectedRecording.call_session_id">
                        {{ selectedRecording.call_session_id }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Connection ID" v-if="selectedRecording.connection_id">
                        {{ selectedRecording.connection_id }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Başlatan" v-if="selectedRecording.initiated_by">
                        {{ selectedRecording.initiated_by }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Kayıt Başlangıç" v-if="selectedRecording.recording_started_at">
                        {{ $moment(selectedRecording.recording_started_at).format("DD.MM.YYYY HH:mm:ss") }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Kayıt Bitiş" v-if="selectedRecording.recording_ended_at">
                        {{ $moment(selectedRecording.recording_ended_at).format("DD.MM.YYYY HH:mm:ss") }}
                    </a-descriptions-item>
                    <a-descriptions-item label="Download URLs" span="2" v-if="selectedRecording.download_urls && Object.keys(selectedRecording.download_urls).length > 0">
                        <div v-for="(url, format) in selectedRecording.download_urls" :key="format">
                            <a-button size="small" @click="playRecording(selectedRecording, format)" :icon="'play-circle'">
                                {{ format.toUpperCase() }} Oynat
                            </a-button>
                        </div>
                    </a-descriptions-item>
                </a-descriptions>
            </div>
        </a-modal>

        <!-- Audio Player Bottom Dock -->
        <div class="audio-player-dock" v-if="showAudioPlayer">
            <div class="audio-player-container">
                <div class="audio-player-info">
                    <i class="material-icons audio-icon pulse">music_note</i>
                    <div class="audio-title-container">
                        <div class="now-playing">Şimdi Çalıyor</div>
                        <div class="audio-title">{{ currentPlayingRecordName }}</div>
                    </div>
                </div>
                <div class="audio-player-wrapper">
                    <audio controls :src="currentPlayingRecordURL" autoplay ref="audioPlayer"></audio>
                </div>
                <div class="audio-player-controls">
                    <a-button type="danger" shape="circle" icon="close" size="large" @click="closeAudioPlayer" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";
import SupsisTable from "../../../../../components/SupsisTable.vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import CommonFilterTool from "../../../Chatbot/pages/archive/CommonFilterTool.vue";
import { WEBPHONE_TELNYX_API } from "../../../../../api/webphone.telnyx";
import log2 from "../../../../../utils/log2";

const log = log2("TelnyxCDR");

export default Vue.extend({
    name: "TelnyxCDR",
    components: {
        SupsisTable,
        SupsisButton,
        CommonFilterTool,
    },
    props: {
        value: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            showDetailModal: false,
            selectedRecording: null,
            recordingData: [],
            searchText: "",
            dateRange: [moment().subtract(7, 'days'), moment()], // Default to last 7 days
            pagination: {
                page: 1,
                perPage: 50,
                total: 0,
            },
            statistics: {
                totalRecordings: 0,
                inboundRecordings: 0,
                outboundRecordings: 0,
                averageDuration: 0,
            },
            // Audio player dock
            showAudioPlayer: false,
            currentPlayingRecordURL: "",
            currentPlayingRecordName: "",
            // Filter system using CommonFilterTool
            filterMetas: [
                {
                    name: "direction",
                    label: "Arama Yönü",
                    materialIcon: "call_split",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [
                        { id: "INBOUND", label: "Gelen" },
                        { id: "OUTBOUND", label: "Giden" },
                    ],
                },
                {
                    name: "from",
                    label: "Arayan Numara",
                    materialIcon: "phone",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "to",
                    label: "Aranan Numara", 
                    materialIcon: "phone_forwarded",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "status",
                    label: "Kayıt Durumu",
                    materialIcon: "info",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [
                        { id: "completed", label: "Tamamlanmış" },
                        { id: "processing", label: "İşleniyor" },
                        { id: "error", label: "Hata" },
                    ],
                },
                {
                    name: "source",
                    label: "Kaynak",
                    materialIcon: "source",
                    controlType: "select",
                    defaultValue: null,
                    options: () => [
                        { id: "call", label: "Arama" },
                        { id: "conference", label: "Konferans" },
                    ],
                },
                {
                    name: "connection_id",
                    label: "Connection ID",
                    materialIcon: "link",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "call_leg_id",
                    label: "Call Leg ID",
                    materialIcon: "call",
                    controlType: "input",
                    defaultValue: null,
                },
                {
                    name: "call_session_id",
                    label: "Call Session ID",
                    materialIcon: "call_merge",
                    controlType: "input",
                    defaultValue: null,
                },
            ],
            breakdownFilter: [],
        };
    },
    computed: {
        channel() {
            return this.value;
        },
    },
    methods: {
        getCols() {
            return [
                {
                    key: "created_at",
                    field: "created_at",
                    title: "Oluşturma Tarihi",
                    width: 140,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.$moment(row.created_at).format("DD.MM.YYYY HH:mm")}</span>;
                    },
                },
                {
                    key: "direction",
                    field: "direction",
                    title: "Yön",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        const direction = this.determineDirection(row);
                        return (
                            <a-tag color={this.getDirectionColor(direction)}>
                                {this.getDirectionTextFromDirection(direction)}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "from",
                    field: "from",
                    title: "Arayan",
                    width: 120,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span>{row.from || "-"}</span>;
                    },
                },
                {
                    key: "to",
                    field: "to",
                    title: "Aranan",
                    width: 120,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span>{row.to || "-"}</span>;
                    },
                },
                {
                    key: "status",
                    field: "status",
                    title: "Durum",
                    width: 100,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <a-tag color={this.getStatusColor(row.status)}>
                                {this.getStatusText(row.status)}
                            </a-tag>
                        );
                    },
                },
                {
                    key: "duration_millis",
                    field: "duration_millis",
                    title: "Süre",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatDuration(row.duration_millis)}</span>;
                    },
                },
                {
                    key: "file_size",
                    field: "file_size",
                    title: "Boyut",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <span>{this.formatFileSize(row.file_size)}</span>;
                    },
                },
                {
                    key: "channels",
                    field: "channels",
                    title: "Kanal",
                    width: 70,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return <span>{row.channels || "-"}</span>;
                    },
                },
                {
                    key: "call_leg_id",
                    field: "call_leg_id",
                    title: "Call Leg ID",
                    width: 120,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span class="text-mono">{row.call_leg_id || "-"}</span>;
                    },
                },
                {
                    key: "call_session_id",
                    field: "call_session_id",
                    title: "Session ID",
                    width: 120,
                    align: "left",
                    renderBodyCell: ({ row }) => {
                        return <span class="text-mono">{row.call_session_id || "-"}</span>;
                    },
                },
                {
                    key: "download_urls",
                    field: "download_urls",
                    title: "Oynat",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        if (!row.download_urls || Object.keys(row.download_urls).length === 0) {
                            return <span>-</span>;
                        }

                        const firstFormat = Object.keys(row.download_urls)[0];
                        return (
                            <SupsisButton
                                onClick={() => this.playRecording(row, firstFormat)}
                                small
                                content="Kayıt Dinle"
                                v-tippy={{ followCursor: true, placement: "bottom-end" }}
                            >
                                <i class="material-icons text-primary">play_circle</i>
                            </SupsisButton>
                        );
                    },
                },
                {
                    title: "İşlemler",
                    key: "actions",
                    width: 80,
                    align: "center",
                    renderBodyCell: ({ row }) => {
                        return (
                            <SupsisButton
                                onClick={() => this.viewDetails(row)}
                                small
                                content="Detay Görüntüle" 
                                v-tippy={{ followCursor: true, placement: "bottom-end" }}
                            >
                                <i class="material-icons text-primary">visibility</i>
                            </SupsisButton>
                        );
                    },
                },
            ];
        },
        async loadRecordingData() {
            if (!this.dateRange || this.dateRange.length !== 2) {
                this.$message.error("Lütfen tarih aralığı seçin");
                return;
            }

            try {
                this.loading = true;

                const [startDate, endDate] = this.dateRange;
                const filters = {
                    filter_created_at_gte: startDate.toISOString(),
                    filter_created_at_lte: endDate.toISOString(),
                    page_number: this.pagination.page,
                    page_size: this.pagination.perPage,
                    sort: "-created_at", // Newest first
                    ...this.generateAPIFilters(),
                };

                // Add search term handling for specific fields
                if (this.searchText && this.searchText.trim()) {
                    const searchTerm = this.searchText.trim();
                    // Since Telnyx doesn't have a general search, we'll try to identify what type of ID this is
                    if (searchTerm.startsWith('leg_')) {
                        filters.filter_call_leg_id = searchTerm;
                    } else if (searchTerm.startsWith('session_')) {
                        filters.filter_call_session_id = searchTerm;
                    } else if (searchTerm.length > 10) {
                        // Assume it's a connection ID if it's long enough
                        filters.filter_connection_id = searchTerm;
                    }
                }

                log("Loading recordings with filters:", filters);

                const response = await WEBPHONE_TELNYX_API.recordings.getCDRList(this.channel._id, filters);

                if (response && response.cdrs) {
                    let recordings = response.cdrs.map((recording, index) => ({
                        ...recording,
                        _id: recording.id || index.toString(),
                        // Convert duration from milliseconds to seconds for consistency
                        duration_seconds: recording.duration_millis ? Math.round(recording.duration_millis / 1000) : 0,
                    }));

                    // Apply client-side filters (direction, from, to)
                    recordings = this.applyClientSideFilters(recordings);

                    this.recordingData = recordings;

                    // Update pagination with server-side total (not filtered total)
                    this.pagination = {
                        ...this.pagination,
                        total: response.total, // Server'dan gelen toplam sayı
                    };
                } else {
                    this.recordingData = [];
                    this.pagination = {
                        ...this.pagination,
                        total: 0,
                    };
                }

                this.calculateStatistics(this.recordingData);
            } catch (error) {
                console.error("Recording verileri yüklenirken hata:", error);
                this.$message.error("Recording verileri yüklenirken hata oluştu");
                this.recordingData = [];
                this.pagination.total = 0;
            } finally {
                this.loading = false;
            }
        },
        generateAPIFilters() {
            const apiFilters = {};

            this.breakdownFilter.forEach((filter) => {
                if (filter.filterValue !== null && filter.filterValue !== undefined && filter.filterValue !== "") {
                    // Direction frontend'de filtreleneceği için API'ye gönderilmez (Telnyx API'sinde direction filter yok)
                    if (filter.name === 'direction') {
                        return;
                    }
                    
                    // from ve to alanları artık server-side'da filtrelenecek
                    apiFilters[`filter_${filter.name}`] = filter.filterValue;
                }
            });

            return apiFilters;
        },
        
        applyClientSideFilters(recordings) {
            let filtered = recordings;

            this.breakdownFilter.forEach((filter) => {
                if (filter.filterValue !== null && filter.filterValue !== undefined && filter.filterValue !== "") {
                    // Sadece direction filter'ı client-side'da uygulanıyor 
                    // (Telnyx API'sinde direction filter olmadığı için)
                    if (filter.name === 'direction') {
                        filtered = filtered.filter(record => {
                            const direction = this.determineDirection(record);
                            return direction === filter.filterValue;
                        });
                    }
                    // from ve to artık server-side'da filtreleniyor, client-side'da işlem yapmaya gerek yok
                }
            });

            return filtered;
        },
        calculateStatistics(records) {
            this.statistics.totalRecordings = records.length;
            
            // Direction based on phone number patterns
            let inbound = 0;
            let outbound = 0;
            
            records.forEach(record => {
                const direction = this.determineDirection(record);
                if (direction === 'INBOUND') {
                    inbound++;
                } else {
                    outbound++;
                }
            });
            
            this.statistics.inboundRecordings = inbound;
            this.statistics.outboundRecordings = outbound;

            // Calculate average duration for all recordings
            if (records.length > 0) {
                const avgDurationMillis = records.reduce((sum, record) => sum + (record.duration_millis || 0), 0) / records.length;
                this.statistics.averageDuration = Math.round(avgDurationMillis / 1000);
            } else {
                this.statistics.averageDuration = 0;
            }
        },
        
        determineDirection(record) {
            // Türk numara formatlarına göre yön belirleme
            const from = record.from || '';
            const to = record.to || '';
            
            // Türk numarası pattern: +90 ile başlayan veya 05 ile başlayan
            const isTurkishNumber = (number) => {
                return number.startsWith('+90') || number.startsWith('90') || number.startsWith('05') || number.startsWith('5');
            };
            
            const isInternationalNumber = (number) => {
                return number.startsWith('+') && !number.startsWith('+90');
            };
            
            // Eğer arayan Türk numarası değil ve aranan Türk numarasıysa -> INBOUND (gelen)
            if (!isTurkishNumber(from) && isTurkishNumber(to)) {
                return 'INBOUND';
            }
            
            // Eğer arayan Türk numarası ve aranan Türk numarası değilse -> OUTBOUND (giden)  
            if (isTurkishNumber(from) && !isTurkishNumber(to)) {
                return 'OUTBOUND';
            }
            
            // Eğer arayan uluslararası numara ise -> INBOUND (gelen)
            if (isInternationalNumber(from)) {
                return 'INBOUND';
            }
            
            // Varsayılan olarak OUTBOUND
            return 'OUTBOUND';
        },
        onDateRangeChange() {
            this.pagination.page = 1;
            this.loadRecordingData();
        },
        onSearch() {
            this.pagination.page = 1;
            this.loadRecordingData();
        },
        onPaginationChange(newPagination) {
            log.info("onPaginationChange", newPagination);

            this.pagination = {
                ...newPagination,
                total: this.pagination.total, // Mevcut total'ı koru
            };
            this.loadRecordingData();
        },
        addFilter(name, value) {
            log.info("addFilter", name, value);
            const meta = this.filterMetas.find((x) => x.name === name);
            const filter = this.breakdownFilter.find((x) => x.name === name);

            if (filter) {
                this.breakdownFilter = this.breakdownFilter.map((x) => {
                    if (x.name === name) {
                        x.filterValue = value || meta.defaultValue;
                    }
                    return x;
                });
            } else {
                this.breakdownFilter.push({
                    name: meta.name,
                    label: meta.label,
                    filterValue: value || meta.defaultValue,
                    materialIcon: meta.materialIcon,
                    controlType: meta.controlType,
                    options: meta.options,
                });
            }
        },
        removeFilter(name) {
            log.info("removeFilter", name);
            this.breakdownFilter = this.breakdownFilter.filter((x) => x.name !== name);
        },
        filterLabelFormatter(name, value) {
            const filter = this.filterMetas.find((x) => x.name === name);
            log.info("filterLabelFormatter", { name, value, filter });

            const isSelect = filter.controlType === "select";
            if (isSelect) {
                const option = filter.options().find((x) => x.id === value);
                if (option) {
                    return `${filter.label}: ${option.label}`;
                }
            }

            if (value) {
                return `${filter.label}: ${value}`;
            }
            return filter.label;
        },
        clearFilters() {
            log.info("clearFilters");
            this.breakdownFilter = [];
            this.searchText = "";
            this.dateRange = [moment().subtract(7, 'days'), moment()]; // Reset to last 7 days
        },
        refreshData() {
            this.loadRecordingData();
        },
        exportRecordings() {
            if (this.recordingData.length === 0) {
                this.$message.warning("Dışa aktarılacak veri bulunamadı");
                return;
            }

            const csvContent = this.generateCSV(this.recordingData);
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const link = document.createElement("a");
            const url = URL.createObjectURL(blob);
            link.setAttribute("href", url);
            link.setAttribute("download", `telnyx_recordings_export_${moment().format("YYYY-MM-DD")}.csv`);
            link.style.visibility = "hidden";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            this.$message.success(`${this.recordingData.length} kayıt dışa aktarıldı`);
        },
        generateCSV(data) {
            const headers = [
                "Recording ID",
                "Oluşturma Tarihi",
                "Yön",
                "Arayan",
                "Aranan",
                "Durum",
                "Kaynak",
                "Süre (saniye)",
                "Dosya Boyutu",
                "Call Leg ID",
                "Call Session ID",
            ];
            const rows = data.map((record) => [
                record.id,
                moment(record.created_at).format("DD.MM.YYYY HH:mm:ss"),
                this.getDirectionTextFromDirection(this.determineDirection(record)),
                record.from || "",
                record.to || "",
                this.getStatusText(record.status),
                this.getSourceText(record.source),
                Math.round((record.duration_millis || 0) / 1000),
                this.formatFileSize(record.file_size),
                record.call_leg_id || "",
                record.call_session_id || "",
            ]);

            return [headers, ...rows].map((row) => row.join(",")).join("\n");
        },
        viewDetails(recording) {
            this.selectedRecording = recording;
            this.showDetailModal = true;
        },
        async playRecording(recording, format = null) {
            if (!recording.download_urls || Object.keys(recording.download_urls).length === 0) {
                this.$message.error("Bu kayıt için indirme URL'i bulunamadı");
                return;
            }

            // Use specified format or first available format
            const selectedFormat = format || Object.keys(recording.download_urls)[0];
            const recordingUrl = recording.download_urls[selectedFormat];

            if (!recordingUrl) {
                this.$message.error("Kayıt URL'i bulunamadı");
                return;
            }

            try {
                this.currentPlayingRecordURL = recordingUrl;
                this.currentPlayingRecordName = `${this.$moment(recording.created_at).format("DD.MM.YYYY HH:mm")} - ${selectedFormat.toUpperCase()}`;
                this.showAudioPlayer = true;

                // Focus on audio player
                this.$nextTick(() => {
                    if (this.$refs.audioPlayer) {
                        this.$refs.audioPlayer.focus();
                    }
                });
            } catch (error) {
                console.error("Kayıt oynatılırken hata:", error);
                this.$message.error("Kayıt oynatılırken hata oluştu");
            }
        },
        closeAudioPlayer() {
            // Pause audio and reset
            if (this.$refs.audioPlayer) {
                this.$refs.audioPlayer.pause();
            }
            this.showAudioPlayer = false;
            this.currentPlayingRecordURL = "";
            this.currentPlayingRecordName = "";
        },
        getStatusText(status) {
            const statuses = {
                completed: "Tamamlandı",
                processing: "İşleniyor",
                error: "Hata",
            };
            return statuses[status] || status;
        },
        getStatusColor(status) {
            const colors = {
                completed: "green",
                processing: "orange",
                error: "red",
            };
            return colors[status] || "default";
        },
        getSourceText(source) {
            const sources = {
                call: "Arama",
                conference: "Konferans",
            };
            return sources[source] || source;
        },
        getSourceColor(source) {
            const colors = {
                call: "blue",
                conference: "purple",
            };
            return colors[source] || "default";
        },
        getDirectionTextFromDirection(direction) {
            const directions = {
                INBOUND: "Gelen",
                OUTBOUND: "Giden",
            };
            return directions[direction] || direction;
        },
        getDirectionColor(direction) {
            const colors = {
                INBOUND: "green",
                OUTBOUND: "blue",
            };
            return colors[direction] || "default";
        },
        formatDuration(millis) {
            const seconds = Math.round((millis || 0) / 1000);
            if (seconds === 0) return "0s";
            const minutes = Math.floor(seconds / 60);
            const secs = seconds % 60;
            return minutes > 0 ? `${minutes}dk ${secs}s` : `${secs}s`;
        },
        formatFileSize(bytes) {
            if (!bytes) return "0 B";
            const sizes = ["B", "KB", "MB", "GB"];
            const i = Math.floor(Math.log(bytes) / Math.log(1024));
            return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
        },
    },
    mounted() {
        this.loadRecordingData();
    },
    beforeDestroy() {
        // Sayfa kapanırken ses oynatıcıyı temizle
        this.closeAudioPlayer();
    },
    watch: {
        // Modal açıldığında/kapatıldığında ses oynatıcılarını kontrol et
        showDetailModal(newVal) {
            if (!newVal) {
                // Modal kapandığında dock audio player'ı çalışır durumda bırak
            } else {
                // Modal açıldığında dock audio player'ı durdur
                if (this.showAudioPlayer && this.$refs.audioPlayer) {
                    this.$refs.audioPlayer.pause();
                }
            }
        },
        breakdownFilter: {
            handler() {
                this.pagination.page = 1;
                this.loadRecordingData();
            },
            deep: true,
        },
    },
});
</script>

<style scoped lang="scss">
#telnyx-cdr {
    padding: 20px;
    background: var(--color-bg-default);
    height: 100%;

    .page-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid var(--color-border-default);

        .left {
            h3 {
                margin: 0 0 5px 0;
                color: var(--color-text-primary);
                font-size: 20px;
                font-weight: 600;
            }

            p {
                margin: 0;
                color: var(--color-text-secondary);
                font-size: 14px;
            }
        }

        .right {
            display: flex;
            gap: 10px;
        }
    }

    .toolbar {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 0;
        gap: 5px;
        margin-bottom: 20px;

        .toolbar-filter {
            display: flex;
            align-items: center;
            gap: 15px;
            flex: 1;
            flex-wrap: wrap;

            .search-input {
                margin: 0 !important;
            }

            .date-filter-inline {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                background: #f5f5f5;
                border-radius: 6px;
                border: 1px solid #d9d9d9;

                .date-label {
                    font-weight: 500;
                    color: #333;
                    white-space: nowrap;
                }

                .date-required {
                    color: #ff4d4f;
                    font-weight: bold;
                }
            }
        }

        .toolbar-actions {
            display: flex;
            gap: 5px;
        }

        .common-filter-tool {
            border-bottom: none !important;
            padding: unset !important;
        }
    }

    .statistics {
        margin-bottom: 20px;

        .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 20px;

            .stat-card {
                background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
                border-radius: 16px;
                padding: 24px;
                display: flex;
                align-items: center;
                gap: 20px;
                box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
                border: 1px solid #e2e8f0;
                transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                position: relative;
                overflow: hidden;

                &::before {
                    content: "";
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    transition: height 0.3s ease;
                }

                &:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);

                    &::before {
                        height: 6px;
                    }

                    .stat-icon {
                        transform: scale(1.1);
                    }
                }

                &.total-recordings {
                    &::before {
                        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #8b5cf6, #7c3aed);
                        color: white;
                    }

                    .stat-value {
                        color: #7c3aed;
                    }
                }

                &.inbound-recordings {
                    &::before {
                        background: linear-gradient(135deg, #10b981, #059669);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #10b981, #059669);
                        color: white;
                    }

                    .stat-value {
                        color: #059669;
                    }

                    .stat-percentage {
                        color: #10b981;
                        font-size: 12px;
                        font-weight: 600;
                        margin-top: 4px;
                    }
                }

                &.outbound-recordings {
                    &::before {
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                        color: white;
                    }

                    .stat-value {
                        color: #1d4ed8;
                    }

                    .stat-percentage {
                        color: #3b82f6;
                        font-size: 12px;
                        font-weight: 600;
                        margin-top: 4px;
                    }
                }

                &.total-duration {
                    &::before {
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #3b82f6, #1d4ed8);
                        color: white;
                    }

                    .stat-value {
                        color: #1d4ed8;
                    }
                }

                &.average-duration {
                    &::before {
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                    }

                    .stat-icon {
                        background: linear-gradient(135deg, #f59e0b, #d97706);
                        color: white;
                    }

                    .stat-value {
                        color: #d97706;
                    }
                }

                .stat-icon {
                    width: 56px;
                    height: 56px;
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    transition: all 0.3s ease;

                    .material-icons {
                        font-size: 28px;
                    }
                }

                .stat-content {
                    flex: 1;
                    min-width: 0;

                    .stat-value {
                        font-size: 32px;
                        font-weight: 700;
                        line-height: 1.2;
                        margin-bottom: 4px;
                        font-family: "SF Pro Display", -apple-system, BlinkMacSystemFont, sans-serif;
                    }

                    .stat-label {
                        font-size: 14px;
                        font-weight: 500;
                        color: #64748b;
                        margin-bottom: 2px;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                    }

                    .stat-unit {
                        font-size: 12px;
                        color: #94a3b8;
                        font-weight: 500;
                    }
                }
            }
        }

        // Responsive design
        @media (max-width: 768px) {
            .stats-grid {
                grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                gap: 15px;

                .stat-card {
                    padding: 20px;
                    gap: 15px;

                    .stat-icon {
                        width: 48px;
                        height: 48px;
                        border-radius: 12px;

                        .material-icons {
                            font-size: 24px;
                        }
                    }

                    .stat-content .stat-value {
                        font-size: 28px;
                    }
                }
            }
        }

        @media (max-width: 480px) {
            .stats-grid {
                grid-template-columns: 1fr;
                gap: 12px;

                .stat-card {
                    padding: 16px;
                    gap: 12px;

                    .stat-icon {
                        width: 40px;
                        height: 40px;
                        border-radius: 10px;

                        .material-icons {
                            font-size: 20px;
                        }
                    }

                    .stat-content .stat-value {
                        font-size: 24px;
                    }
                }
            }
        }
    }

    .content {
        background: var(--color-bg-container);
        border-radius: 6px;
        border: 1px solid var(--color-border-default);
        overflow: hidden;
    }

    .text-mono {
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
        font-size: 12px;
        color: #666;
    }

    .audio-player-dock {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 15px 0;
        z-index: 1000;

        .audio-player-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            max-width: 800px;
            margin: 0 auto;
            padding: 15px 25px;
            background: rgb(255 229 224);
            border-radius: 12px;

            .audio-player-info {
                display: flex;
                align-items: center;
                gap: 12px;
                flex: 1;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;

                .audio-icon {
                    font-size: 28px;
                    color: #3a7bd5;
                    background: white;
                    border-radius: 50%;
                    padding: 8px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                    animation: pulse 2s infinite;
                }

                .audio-title-container {
                    .now-playing {
                        font-weight: 600;
                        color: #3a7bd5;
                        font-size: 12px;
                        text-transform: uppercase;
                        letter-spacing: 1px;
                        margin-bottom: 3px;
                    }

                    .audio-title {
                        font-weight: 500;
                        color: #333;
                        font-size: 14px;
                    }
                }
            }

            .audio-player-wrapper {
                flex: 2;
                max-width: 500px;
                padding: 0 20px;

                audio {
                    width: 100%;
                    height: 40px;
                    border-radius: 8px;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }
            }

            .audio-player-controls {
                margin-left: 15px;

                button {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 40px;
                    height: 40px;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
                    transition: all 0.3s ease;

                    &:hover {
                        transform: scale(1.1);
                        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.2);
                    }
                }
            }
        }
    }

    @keyframes pulse {
        0% {
            transform: scale(1);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        }
        50% {
            transform: scale(1.05);
            box-shadow: 0 6px 15px rgba(58, 123, 213, 0.4);
        }
        100% {
            transform: scale(1);
            box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
        }
    }
}
</style> 